import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import { Warning as WarningIcon } from "@mui/icons-material";
import { SubscriptionInfo } from "../../api/SubscriptionAPI";

interface SubscriptionExpiredModalProps {
  open: boolean;
  subscriptionInfo: SubscriptionInfo | null;
  onClose: () => void;
}

const SubscriptionExpiredModal: React.FC<SubscriptionExpiredModalProps> = ({
  open,
  subscriptionInfo,
  onClose,
}) => {
  const handleWhatsApp = () => {
    const planName = subscriptionInfo?.subscription?.plan?.name || "seu plano";
    const message = encodeURIComponent(
      `Olá! Gostaria de renovar minha assinatura do plano ${planName}. Podem me ajudar com o processo de renovação?`
    );

    const whatsappNumber = "5588992429813";
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${message}`;

    window.open(whatsappUrl, "_blank");
    onClose();
  };

  const getStatusMessage = () => {
    if (!subscriptionInfo?.subscription) {
      return "Sua assinatura não foi encontrada.";
    }

    const status = subscriptionInfo.subscription.status;

    switch (status) {
      case 0:
        return "Seu período gratuito expirou.";
      case 2:
        return "Sua assinatura está em atraso.";
      case 3:
        return "Sua assinatura foi cancelada.";
      default:
        return "Sua assinatura está vencida.";
    }
  };

  const getPlanInfo = () => {
    if (!subscriptionInfo?.subscription?.plan) return null;

    const plan = subscriptionInfo.subscription.plan;
    return {
      name: plan.name,
      price: plan.price,
      interval: plan.interval === 0 ? "mês" : "ano",
    };
  };

  const planInfo = getPlanInfo();

  if (!subscriptionInfo?.subscription) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 1,
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
          <WarningIcon sx={{ fontSize: 40, color: "error.main", mr: 1 }} />
          <Typography variant="h5" color="error.main" fontWeight="bold">
            Acesso Bloqueado
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ textAlign: "center", pb: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body1" fontWeight="medium">
            {getStatusMessage()}
          </Typography>
        </Alert>

        {planInfo && (
          <Box sx={{ mb: 2, p: 2, bgcolor: "#f8f9fa", borderRadius: 1 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Seu Plano: {planInfo.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              R$ {planInfo.price.toFixed(2).replace(".", ",")} /{" "}
              {planInfo.interval}
            </Typography>
          </Box>
        )}

        <Typography variant="body2" color="text.secondary">
          Entre em contato conosco via WhatsApp para renovar sua assinatura e
          continuar usando a plataforma.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", pb: 3, px: 3 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleWhatsApp}
          sx={{
            minWidth: 200,
            py: 1.5,
            backgroundColor: "#25D366", // Cor do WhatsApp
            "&:hover": {
              backgroundColor: "#128C7E",
            },
          }}
        >
          Renovar via WhatsApp
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionExpiredModal;
